import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { LuChevronDown, LuChevronUp } from "react-icons/lu";
import { isCreditRefusal } from "../../../utils/insufficientBalance";
import TalkingVoicePicker from "./TalkingVoicePicker";
import {
  estimateTalkingAvatarCredit,
  fetchOrgPrivateVoices,
  fetchTalkingAvatarModels,
  fetchTalkingAvatarVoiceProviders,
  submitTalkingAvatarJob,
} from "./talkingAvatarApi";
import {
  describeTalkingAvatarError,
  seedanceAvatarUnsupportedText,
  VEO_FALLBACK_MODEL,
} from "./talkingAvatarErrors";
import { pickNarrationSound } from "./talkingAvatarInsert";
import {
  activeJobForScene,
  getTalkingAvatarPrefs,
  setTalkingAvatarPrefs,
  useTalkingAvatarJobsSelector,
} from "./talkingAvatarJobStore";
import {
  aspectOptions,
  coerceAspect,
  isAudioDriven,
  isSingleTake,
  lengthInfo,
} from "./talkingAvatarMath";
import {
  azurePickerVoices,
  resolveInitialVoice,
  sameVoice,
  voiceGenderMismatch,
  voiceProviderOf,
  VOICE_PROVIDER_AZURE,
  VOICE_PROVIDER_ELEVENLABS,
} from "./talkingAvatarVoice";
import { tt } from "./talkingAvatarI18n";
import { watchTalkingAvatarJob } from "./useTalkingAvatarJob";
import { startTalkingAvatarTestRun } from "./talkingAvatarTestRun";
import "./_talkingAvatar.scss";
import { getContentLocale } from "../../../../common/content_language";

// Step 2 of "Avatar Anlatımı" in talking-avatar mode: the narration text (the
// tab's own narrationText, so switching methods keeps it), the voice the speech
// is converted to (ElevenLabs, or Azure — Beta), transparent background, and —
// under "Gelişmiş ayarlar" — model, aspect ratio and spoken language. Lip-sync
// settings do not apply here and are not shown.
//
// An audio-driven model (capability.audioDriven, e.g. OmniHuman) works the
// other way round: the narration is first synthesized in the chosen voice and
// the avatar is animated to it in ONE take — no conversion, no segment chain.
// The form is the same; only its notes read differently.
//
// The footer's "Avatar Oluştur" calls submit() through the ref; this panel
// reports { canCreate, seconds, submitting, checkCredit } to the tab, which
// forwards them to the modal footer exactly like the lip-sync flow does.

const CREDIT_DEBOUNCE_MS = 700;

const LANGUAGE_OPTIONS = [
  { value: "tr-TR", key: "LanguageNames.Turkish", fallback: "Türkçe" },
  { value: "en-US", key: "LanguageNames.English", fallback: "English (US)" },
  { value: "en-GB", key: "LanguageNames.EnglishUK", fallback: "English (UK)" },
  { value: "de-DE", key: "LanguageNames.German", fallback: "Deutsch" },
  { value: "es-ES", key: "LanguageNames.Spanish", fallback: "Español" },
  { value: "fr-FR", key: "LanguageNames.French", fallback: "Français" },
  { value: "pt-BR", key: "LanguageNames.Portuguese", fallback: "Português (BR)" },
  { value: "ar-SA", key: "LanguageNames.Arabic", fallback: "العربية" },
  { value: "az-AZ", key: "LanguageNames.Azerbaijani", fallback: "Azərbaycanca" },
];
const LANGUAGE_ALIASES = {
  tr: "tr-TR",
  en: "en-US",
  "en-uk": "en-GB",
  de: "de-DE",
  es: "es-ES",
  fr: "fr-FR",
  pt: "pt-BR",
  ar: "ar-SA",
  az: "az-AZ",
};

// A speech language for the job; "multilingual" (ElevenLabs) says nothing.
function toLanguage(lang) {
  const l = String(lang || "")
    .trim()
    .toLowerCase();
  if (!l || l === "multilingual") return null;
  const exact = LANGUAGE_OPTIONS.find((o) => o.value.toLowerCase() === l);
  if (exact) return exact.value;
  return LANGUAGE_ALIASES[l] || LANGUAGE_ALIASES[l.split("-")[0]] || null;
}

const ASPECT_LABELS = {
  "16:9": ["TalkingAvatar.Aspect.Wide", "Yatay (16:9)"],
  "9:16": ["TalkingAvatar.Aspect.Tall", "Dikey (9:16)"],
  "1:1": ["TalkingAvatar.Aspect.Square", "Kare (1:1)"],
};

const MISSING_COPY = {
  Model: "uygun bir model seçin",
  Avatar: "bir avatar seçin",
  AvatarUploading: "avatar yüklemesinin bitmesini bekleyin",
  AvatarUploadFailed: "avatar yüklenemedi, tekrar yükleyin",
  Text: "anlatım metni girin",
  TextOver: "metni kısaltın",
  TextPart: "uzun cümleleri bölün",
  Voice: "bir ses seçin",
  Aspect: "desteklenen bir en/boy oranı seçin",
  ActiveJob: "bu sahnedeki işin bitmesini bekleyin",
};

// avatarSupported: false = the model cannot be used with THIS avatar (Seedance
// needs an AI-created library avatar); the list says so per avatar.
const usable = (m) => !!m && m.available && m.priced && m.avatarSupported !== false;

// Which avatar the models are asked about (Seedance needs an AI-created library
// avatar): a library / AI avatar by its id, an own photo or video as an upload,
// nothing yet → no query (the list is then avatar-agnostic).
function modelsQueryFor(source) {
  const payload = source && source.payload;
  if (!payload) return {};
  if (payload.avatarId !== undefined && payload.avatarId !== null) {
    return { avatarId: payload.avatarId };
  }
  return { avatarSource: "upload" };
}

function pickDefaultModel(models, preferred) {
  const pref = models.find((m) => m.id === preferred && usable(m));
  if (pref) return pref.id;
  const veo = models.find((m) => /^veo/i.test(m.id) && usable(m));
  if (veo) return veo.id;
  const any = models.find(usable) || models.find((m) => m.available) || models[0];
  return any ? any.id : null;
}

function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0; // eslint-disable-line no-bitwise
  return (h >>> 0).toString(36); // eslint-disable-line no-bitwise
}
const newNonce = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// Hash of the whole request (voice provider included) + the attempt's nonce.
export const talkingAvatarIdempotencyKey = (body, nonce) =>
  `ta-${hashString(JSON.stringify(body))}-${nonce}`;

function readTrainingId() {
  try {
    return (
      new URLSearchParams(window.location.search).get("id") || window.idealStudioTrainingId || ""
    );
  } catch (e) {
    return "";
  }
}

const formatCredits = (n) => {
  const v = Number(n) || 0;
  return v >= 10 ? String(Math.round(v)) : String(Math.round(v * 10) / 10);
};

const TalkingVideoPanel = forwardRef(function TalkingVideoPanel(
  {
    stepRef,
    locked,
    navId,
    sceneIndex,
    source,
    text,
    onTextChange,
    soundList,
    loadSceneNarrationText,
    transparentBg,
    onTransparentBgChange,
    aspectRatio,
    onAspectRatioChange,
    voiceProfile,
    onStatusChange,
    onInsufficientBalance,
    testVideoUrl,
    avatarGender,
  },
  ref
) {
  const textValue = text || "";
  const trimmed = textValue.trim();
  const textareaRef = useRef(null);
  const textRef = useRef(textValue);
  textRef.current = textValue;

  // ── Models ──────────────────────────────────────────────────────────────
  // Asked about the chosen avatar and asked again when it changes; a model the
  // new avatar cannot use (Seedance → own upload) gives way to the default.
  const [models, setModels] = useState(null);
  const [modelsError, setModelsError] = useState(false);
  const [modelId, setModelId] = useState(null);
  const sourcePayload = source && source.payload;
  const modelsQuery = useMemo(
    () => modelsQueryFor(source),
    [
      sourcePayload && sourcePayload.avatarId,
      sourcePayload && (sourcePayload.imageUrl || sourcePayload.videoUrl),
    ]
  );
  const modelsQueryKey = JSON.stringify(modelsQuery);
  // Only the answer for the avatar on screen counts: a slower one for the
  // previous avatar must not bring back what that avatar could use.
  const latestModelsKeyRef = useRef(modelsQueryKey);
  latestModelsKeyRef.current = modelsQueryKey;
  const loadModels = useCallback(() => {
    const key = modelsQueryKey;
    setModelsError(false);
    fetchTalkingAvatarModels(modelsQuery)
      .then((list) => {
        if (latestModelsKeyRef.current !== key) return;
        setModels(list);
        setModelId((cur) =>
          cur && list.some((m) => m.id === cur && usable(m))
            ? cur
            : pickDefaultModel(list, getTalkingAvatarPrefs().model)
        );
      })
      .catch(() => {
        if (latestModelsKeyRef.current !== key) return;
        setModels([]);
        setModelsError(true);
      });
  }, [modelsQueryKey]);
  useEffect(() => {
    loadModels();
  }, [loadModels]);
  const model = useMemo(
    () => (models || []).find((m) => m.id === modelId) || null,
    [models, modelId]
  );
  const cap = model ? model.capability : null;
  const audioDriven = isAudioDriven(cap);
  const ratioOptions = useMemo(() => aspectOptions(cap), [cap]);
  const aspectAllowed = ratioOptions.some((o) => o.value === aspectRatio);

  // A model that cannot produce the current ratio moves it to one it can.
  useEffect(() => {
    if (!cap) return;
    const next = coerceAspect(aspectRatio, cap);
    if (next !== aspectRatio) onAspectRatioChange?.(next);
  }, [cap]);

  const switchToVeo = useCallback(
    (preferred) => {
      const list = models || [];
      const target =
        list.find((m) => m.id === preferred && usable(m)) ||
        list.find((m) => /^veo/i.test(m.id) && usable(m));
      if (target) setModelId(target.id);
    },
    [models]
  );

  // ── Voices ──────────────────────────────────────────────────────────────
  const [orgVoices, setOrgVoices] = useState(null);
  const [orgError, setOrgError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchOrgPrivateVoices()
      .then((list) => {
        if (!cancelled) setOrgVoices(list);
      })
      .catch(() => {
        if (!cancelled) {
          setOrgVoices([]);
          setOrgError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Voice providers (ElevenLabs, Azure — Beta). Until the list is known (or
  // when it cannot be loaded) ElevenLabs is assumed usable, as it always was,
  // and Azure is not offered.
  const [voiceProviders, setVoiceProviders] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchTalkingAvatarVoiceProviders()
      .then((list) => {
        if (!cancelled) setVoiceProviders(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setVoiceProviders([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const providerUsable = useCallback(
    (id) => {
      const p = (voiceProviders || []).find((x) => x && x.id === id);
      if (!p) return id === VOICE_PROVIDER_ELEVENLABS;
      return !!p.available && !!p.priced;
    },
    [voiceProviders]
  );
  // The Azure voices a scene's Azure narration can be matched against — none
  // while Azure cannot be used, so such a scene preselects nothing. An
  // audio-driven model takes every Azure TTS voice (azurePickerVoices).
  const azureVoices = useMemo(() => {
    const azure = (voiceProviders || []).find((x) => x && x.id === VOICE_PROVIDER_AZURE);
    return azure && azure.available && azure.priced ? azurePickerVoices(azure, audioDriven) : [];
  }, [voiceProviders, audioDriven]);

  const [voice, setVoice] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const voiceTouchedRef = useRef(false);
  const targetSound = useMemo(() => pickNarrationSound(soundList), [soundList]);
  const sceneVoiceData = targetSound ? targetSound.sound.voiceData || null : null;

  // Each scene starts from its own narration's voice (when it is ElevenLabs,
  // or an Azure voice that can convert); a scene without one keeps the voice
  // already picked.
  useEffect(() => {
    voiceTouchedRef.current = false;
  }, [navId]);
  useEffect(() => {
    if (voiceTouchedRef.current) return;
    const v = resolveInitialVoice({
      soundVoiceData: sceneVoiceData,
      voiceProfile,
      orgVoices: orgVoices || [],
      azureVoices,
      avatarGender,
    });
    if (v) setVoice(v);
  }, [navId, sceneVoiceData, voiceProfile, orgVoices, azureVoices, avatarGender]);
  // Leaving an audio-driven model drops an Azure voice the new model cannot
  // convert to (a TTS-only voice such as tr-TR-EmelNeural).
  useEffect(() => {
    if (!voice || voiceProviderOf(voice) !== VOICE_PROVIDER_AZURE || !azureVoices.length) return;
    if (!azureVoices.some((v) => sameVoice({ ...v, provider: VOICE_PROVIDER_AZURE }, voice))) {
      setVoice(null);
    }
  }, [azureVoices, voice]);
  const voiceProvider = voiceProviderOf(voice);

  const handleVoiceChange = useCallback((v) => {
    voiceTouchedRef.current = true;
    setVoice(v);
    setSubmitError(null);
  }, []);

  // ElevenLabs TTS model ('v2_5' | 'v3') for audio-driven models: the course's
  // choice, else the last one used here, else v3. Speech-to-speech ignores it.
  const [elevenLabsModel, setElevenLabsModel] = useState(() => {
    const m =
      (voiceProfile && voiceProfile.elevenLabsModel) || getTalkingAvatarPrefs().elevenLabsModel;
    return m === "v2_5" ? "v2_5" : "v3";
  });
  const handleElevenLabsModelChange = useCallback((m) => {
    setElevenLabsModel(m);
    setTalkingAvatarPrefs({ elevenLabsModel: m });
  }, []);

  // ── Language ────────────────────────────────────────────────────────────
  const defaultLanguage =
    toLanguage(voiceProfile && voiceProfile.voiceLanguage) ||
    toLanguage(sceneVoiceData && sceneVoiceData.language) ||
    toLanguage(getContentLocale()) ||
    "tr-TR";
  const [languageChoice, setLanguageChoice] = useState(null);
  const language = languageChoice || defaultLanguage;

  // ── Narration text prefill (per scene, never over the author's own text) ─
  const [sceneTextLoading, setSceneTextLoading] = useState(false);
  const [sceneTextMissing, setSceneTextMissing] = useState(false);
  const prefillRef = useRef({ navId: undefined, text: null });
  useEffect(() => {
    const prev = prefillRef.current;
    if (prev.navId === navId) return undefined;
    prefillRef.current = { navId, text: prev.text };
    const current = textRef.current;
    const untouched = !current.trim() || (prev.text != null && current === prev.text);
    if (!untouched) return undefined;
    const fromSound = targetSound && targetSound.sound.narrationText;
    if (fromSound && fromSound.trim()) {
      prefillRef.current = { navId, text: fromSound };
      onTextChange?.(fromSound);
      return undefined;
    }
    if (!loadSceneNarrationText) return undefined;
    let cancelled = false;
    setSceneTextLoading(true);
    Promise.resolve(loadSceneNarrationText())
      .then((t) => {
        const now = textRef.current;
        if (cancelled || !t || !(now.trim() === "" || now === prev.text)) return;
        prefillRef.current = { navId, text: t };
        onTextChange?.(t);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSceneTextLoading(false);
      });
    return () => {
      cancelled = true;
      // Interrupted before the text arrived: let the next run try again.
      if (prefillRef.current.navId === navId && prefillRef.current.text === prev.text) {
        prefillRef.current = prev;
      }
    };
  }, [navId]);

  const fetchSceneText = async () => {
    setSceneTextLoading(true);
    setSceneTextMissing(false);
    try {
      const fromServer = loadSceneNarrationText ? await loadSceneNarrationText() : null;
      const t = fromServer || (targetSound && targetSound.sound.narrationText) || null;
      if (t) {
        prefillRef.current = { navId, text: t };
        onTextChange?.(t);
      } else {
        setSceneTextMissing(true);
      }
    } catch (e) {
      setSceneTextMissing(true);
    } finally {
      setSceneTextLoading(false);
    }
  };

  // ── Credit quote (debounced; a newer edit aborts the older request) ──────
  // The voice provider prices the conversion: switching it re-quotes.
  const quoteKey = model
    ? JSON.stringify([model.id, trimmed, !!transparentBg, language, voiceProvider])
    : null;
  const canQuote = usable(model) && !!trimmed;
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const latestQuoteKeyRef = useRef(null);
  useEffect(() => {
    latestQuoteKeyRef.current = quoteKey;
    if (!canQuote) {
      setQuoteLoading(false);
      return undefined;
    }
    const key = quoteKey;
    const params = {
      model: model.id,
      text: trimmed,
      removeBackground: !!transparentBg,
      language,
      voiceProvider,
    };
    let controller = null;
    setQuoteLoading(true);
    const timer = setTimeout(() => {
      controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      estimateTalkingAvatarCredit(params, { signal: controller ? controller.signal : undefined })
        .then((data) => {
          if (latestQuoteKeyRef.current !== key) return;
          setQuote({ key, data });
          setQuoteError(null);
        })
        .catch((err) => {
          if ((controller && controller.signal.aborted) || latestQuoteKeyRef.current !== key)
            return;
          setQuoteError({ key, code: err && err.code });
        })
        .finally(() => {
          if (latestQuoteKeyRef.current === key) setQuoteLoading(false);
        });
    }, CREDIT_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      if (controller) controller.abort();
    };
  }, [quoteKey, canQuote]);
  const currentQuote = quote && quote.key === quoteKey ? quote.data : null;
  const currentQuoteError = quoteError && quoteError.key === quoteKey ? quoteError : null;
  const priceMissing = !!currentQuoteError && currentQuoteError.code === "PRICE_LIST_MISSING";

  const info = lengthInfo(textValue, cap, currentQuote);

  // ── Can we create? ────────────────────────────────────────────────────────
  // Only "does THIS scene have a job still running" — other jobs' progress
  // ticks do not re-render the panel.
  const blockingJob = useTalkingAvatarJobsSelector((all) => {
    const activeJob = activeJobForScene(navId, all);
    return !!activeJob && activeJob.status !== "unknown";
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // No model to use (the list failed, or none is available and priced): the
  // reason and the retry sit under "Gelişmiş ayarlar" — open it.
  const noUsableModel = modelsError || (Array.isArray(models) && !models.some(usable));
  useEffect(() => {
    if (noUsableModel) setAdvancedOpen(true);
  }, [noUsableModel]);

  const missing = [];
  if (!usable(model) || priceMissing) missing.push("Model");
  if (!source || !source.ready) {
    missing.push(
      source && source.pending
        ? "AvatarUploading"
        : source && source.reason === "uploadFailed"
          ? "AvatarUploadFailed"
          : "Avatar"
    );
  }
  if (!info.words) missing.push("Text");
  else if (info.partTooLong) missing.push("TextPart");
  else if (info.overLimit) missing.push("TextOver");
  if (!voice || !voice.providerId || !providerUsable(voiceProvider)) missing.push("Voice");
  if (cap && !aspectAllowed) missing.push("Aspect");
  if (blockingJob) missing.push("ActiveJob");
  const canCreate = missing.length === 0 && !submitting;

  // Idempotency: the same attempt re-sent after a network failure or a 5xx
  // gets the same key (the lost/failed response may have created a job — the
  // retry finds it instead of paying twice); any edit, an accepted submission,
  // a 4xx refusal or the job's failure starts a new attempt (see submit).
  const nonceRef = useRef(newNonce());
  const voiceKey = voice
    ? `${voiceProvider}:${voice.isPrivate ? "p" : "s"}:${voice.providerId}`
    : "";
  const sourceKey = source && source.payload ? JSON.stringify(source.payload) : "";
  useEffect(() => {
    nonceRef.current = newNonce();
  }, [modelId, trimmed, voiceKey, aspectRatio, transparentBg, language, sourceKey, navId]);
  // A refusal stays on screen until the author changes something (not when the
  // error's own action — e.g. clearing the rejected voice — changes the form).
  useEffect(() => {
    setSubmitError(null);
  }, [sourceKey, navId]);

  const latestRef = useRef({});
  latestRef.current = {
    model,
    text: trimmed,
    transparentBg,
    voice,
    elevenLabsModel,
    aspectRatio,
    language,
    source,
    navId,
    sceneIndex,
    canCreate,
    soundList,
    testVideoUrl,
    estimatedSeconds: info.estimatedSeconds,
  };

  const runAction = useCallback(
    (action) => {
      if (!action) return;
      if (action.type === "focusText") {
        if (textareaRef.current) textareaRef.current.focus();
      } else if (action.type === "clearVoice") {
        voiceTouchedRef.current = true;
        setVoice(null);
        setPickerOpen(true);
      } else if (action.type === "pickModel" || action.type === "pickAspect") {
        setAdvancedOpen(true);
      } else if (action.type === "switchModel") {
        setSubmitError(null);
        switchToVeo(action.model || VEO_FALLBACK_MODEL);
      }
    },
    [switchToVeo]
  );

  const submittingRef = useRef(false);
  const submit = useCallback(async () => {
    const s = latestRef.current;
    if (submittingRef.current) return;
    if (!s.canCreate) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    const body = {
      trainingId: readTrainingId(),
      navId: s.navId,
      ...(s.sceneIndex >= 1 ? { sceneIndex: s.sceneIndex } : {}),
      model: s.model.id,
      text: s.text,
      voiceId: s.voice.providerId,
      voiceProvider: voiceProviderOf(s.voice),
      ...(isAudioDriven(s.model.capability) &&
      voiceProviderOf(s.voice) === VOICE_PROVIDER_ELEVENLABS
        ? { elevenLabsModel: s.elevenLabsModel }
        : {}),
      aspectRatio: s.aspectRatio,
      removeBackground: !!s.transparentBg,
      avatar: s.source.payload,
      language: s.language,
    };
    body.idempotencyKey = talkingAvatarIdempotencyKey(body, nonceRef.current);
    try {
      const { jobId } = await submitTalkingAvatarJob(body);
      if (!jobId) throw new Error("No job id");
      nonceRef.current = newNonce();
      setTalkingAvatarPrefs({ model: body.model });
      watchTalkingAvatarJob({
        jobId,
        trainingId: body.trainingId,
        navId: body.navId,
        sceneIndex: body.sceneIndex,
        model: body.model,
        modelLabel: s.model.label || body.model,
        aspectRatio: body.aspectRatio,
        removeBackground: body.removeBackground,
        language: body.language,
        text: body.text,
        voice: {
          id: s.voice.id,
          providerId: s.voice.providerId,
          name: s.voice.name,
          isPrivate: !!s.voice.isPrivate,
          provider: body.voiceProvider,
        },
        avatar: { kind: s.source.kind, thumbUrl: s.source.thumbUrl, name: s.source.name },
        aiJobId: s.source.aiJobId || null,
        status: "pending",
        progress: 0,
        createdAt: Date.now(),
      });
    } catch (err) {
      // A 4xx settles this attempt (nothing was created) → next submit is a new
      // attempt. A network failure or a 5xx keeps the key: a 502/504 can hide a
      // job the API did create, and the same key makes the retry find it instead
      // of starting a second paid job (the API dedupes by key).
      if (err && typeof err.status === "number" && err.status >= 400 && err.status < 500) {
        nonceRef.current = newNonce();
      }
      if (err && (err.creditRefusal || isCreditRefusal(err.body))) {
        onInsufficientBalance?.(err.body || { message: err.message });
        return;
      }
      const described = describeTalkingAvatarError(err && err.code, {
        model: s.model && s.model.id,
        estimatedSeconds: err && err.estimatedSeconds,
        maxSeconds: err && err.maxSeconds,
        reason: err && err.reason,
        phase: "submit",
      });
      if (described.code === "INVALID_REQUEST") {
        console.warn("[TalkingAvatar] request failed validation", err && err.body);
      }
      setSubmitError(described);
      // Steps that need no decision run straight away; a model switch waits
      // for the author's click.
      if (described.action && described.action.type !== "switchModel") runAction(described.action);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [onInsufficientBalance, runAction]);

  // "Test üretim (kredisiz)": same record as a real submit, but a local fake
  // job (10 s, the avatar's video + the scene's narration) — no API, no credits.
  const testRun = useCallback(() => {
    const s = latestRef.current;
    if (!s.canCreate || !s.testVideoUrl || submittingRef.current) return false;
    const narration = pickNarrationSound(s.soundList);
    startTalkingAvatarTestRun(
      {
        trainingId: readTrainingId(),
        navId: s.navId,
        ...(s.sceneIndex >= 1 ? { sceneIndex: s.sceneIndex } : {}),
        model: s.model.id,
        modelLabel: s.model.label || s.model.id,
        aspectRatio: s.aspectRatio,
        removeBackground: false,
        language: s.language,
        text: s.text,
        voice: {
          id: s.voice.id,
          providerId: s.voice.providerId,
          name: s.voice.name,
          isPrivate: !!s.voice.isPrivate,
          provider: voiceProviderOf(s.voice),
        },
        avatar: { kind: s.source.kind, thumbUrl: s.source.thumbUrl, name: s.source.name },
      },
      {
        videoUrl: s.testVideoUrl,
        audioUrl: narration && narration.sound && narration.sound.file,
        durationSec: Math.round(s.estimatedSeconds || 0),
      }
    );
    return true;
  }, []);

  useImperativeHandle(ref, () => ({ submit, testRun }), [submit, testRun]);

  // ── Footer report ───────────────────────────────────────────────────────
  const checkCredit = useCallback(() => {
    const s = latestRef.current;
    if (!s.model) return Promise.reject(new Error("No model"));
    return estimateTalkingAvatarCredit({
      model: s.model.id,
      text: s.text,
      removeBackground: !!s.transparentBg,
      language: s.language,
      voiceProvider: voiceProviderOf(s.voice),
    });
  }, []);
  const seconds = info.words ? info.estimatedSeconds : null;
  useEffect(() => {
    onStatusChange?.({
      canCreate,
      seconds,
      submitting,
      checkCredit: canQuote ? checkCredit : null,
    });
  }, [canCreate, seconds, submitting, canQuote, checkCredit, onStatusChange]);
  // Unmounted (job view took over, method switched): leave nothing stale behind.
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  useEffect(
    () => () =>
      onStatusChangeRef.current?.({
        canCreate: false,
        seconds: null,
        submitting: false,
        checkCredit: null,
      }),
    []
  );

  // ── Render ──────────────────────────────────────────────────────────────
  // One summary line: "~15 sn · 18 kelime · 2 segment · ~124 kredi". While the
  // credit is being recalculated (any edit, debounce included) only a spinner
  // shows. The model's maximum is named only near / over it.
  const pendingQuote = canQuote && !currentQuote && !currentQuoteError;
  let summary = null;
  if (cap) {
    if (info.partTooLong) {
      summary = (
        <div className="asm-ta__summary asm-ta__summary--over">
          {
            describeTalkingAvatarError("TALKING_AVATAR_TEXT_TOO_LONG", {
              estimatedSeconds: info.estimatedSeconds,
              maxSeconds: info.maxSeconds,
            }).text
          }
        </div>
      );
    } else if (info.state === "empty") {
      summary = (
        <div className="asm-ta__summary">
          {tt("TalkingAvatar.Text.MeterEmpty", "En fazla {{max}} sn konuşma", {
            max: info.maxSeconds,
          })}
        </div>
      );
    } else if (pendingQuote && info.state !== "over") {
      summary = (
        <div
          className="asm-ta__summary asm-ta__summary--loading"
          role="status"
          aria-label={tt("TalkingAvatar.Credit.Loading", "Kredi hesaplanıyor…")}
        >
          <span className="asm-ta__spinner" aria-hidden="true" />
        </div>
      );
    } else {
      // A single-take model (one call, no chain) names no segment count.
      const parts = [
        isSingleTake(cap)
          ? tt("TalkingAvatar.Text.SummaryBaseSingle", "~{{seconds}} sn · {{words}} kelime", {
              seconds: Math.round(info.estimatedSeconds),
              words: info.words,
            })
          : tt(
              "TalkingAvatar.Text.SummaryBase",
              "~{{seconds}} sn · {{words}} kelime · {{segments}} segment",
              {
                seconds: Math.round(info.estimatedSeconds),
                words: info.words,
                segments: info.segments,
              }
            ),
      ];
      if (priceMissing) parts.push(describeTalkingAvatarError("PRICE_LIST_MISSING").text);
      else if (currentQuote)
        parts.push(
          tt("TalkingAvatar.Text.SummaryCredit", "~{{credits}} kredi", {
            credits: formatCredits(currentQuote.requiredCredits),
          })
        );
      else if (currentQuoteError)
        parts.push(tt("TalkingAvatar.Credit.Error", "Kredi tahmini alınamadı."));
      const notes = [];
      if (info.state === "warn")
        notes.push(
          tt(
            "TalkingAvatar.Text.WarnMax",
            "Model sınırına yaklaşıyorsunuz (en fazla {{max}} sn).",
            {
              max: info.maxSeconds,
            }
          )
        );
      if (info.state === "over")
        notes.push(
          tt(
            "TalkingAvatar.Text.OverMax",
            "Metin bu model için çok uzun (en fazla {{max}} sn); lütfen kısaltın.",
            {
              max: info.maxSeconds,
            }
          )
        );
      const insufficient = !!currentQuote && !currentQuote.hasSufficientBalance;
      if (insufficient)
        notes.push(
          tt("TalkingAvatar.Credit.Insufficient", "Bakiyeniz bu iş için yetersiz görünüyor.")
        );
      const tone =
        info.state === "over" || priceMissing
          ? " asm-ta__summary--over"
          : info.state === "warn" || insufficient
            ? " asm-ta__summary--warn"
            : "";
      summary = (
        <div className={`asm-ta__summary${tone}`}>
          {parts.join(" · ") + (notes.length ? ` — ${notes.join(" ")}` : "")}
        </div>
      );
    }
  }

  // Avatar reasons first: "use Veo for this avatar" is what the author can act on.
  const modelHint = (m) => {
    if (m.avatarSupported === false)
      return seedanceAvatarUnsupportedText(m.avatarUnsupportedReason);
    if (!m.available)
      return tt("TalkingAvatar.Model.Unavailable", "Bu model şu anda kullanılamıyor");
    if (!m.priced) return tt("TalkingAvatar.Model.Unpriced", "Bu model için fiyat tanımlı değil");
    return null;
  };

  return (
    <div
      ref={stepRef}
      className={`add-sound-modal__narration asm-av__step${locked ? " asm-av__step--locked" : ""}`}
    >
      <div className="asm-av__step-head">
        <div className="asm-av__step-head-main">
          <span className="asm-av__step-badge">2</span>
          <div className="asm-av__step-titles">
            <h3 className="asm-av__step-title">{tt("TalkingAvatar.Step.Title", "Metin ve ses")}</h3>
            <p className="asm-av__step-sub">
              {locked
                ? tt("TalkingAvatar.Step.Locked", "Önce bir avatar seçin veya oluşturun")
                : tt("TalkingAvatar.Step.Sub", "Avatarın söyleyeceği metin ve konuşacağı ses")}
            </p>
          </div>
        </div>
      </div>

      <div
        className="add-sound-modal__narration-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <label className="add-sound-modal__narration-label" style={{ margin: 0 }}>
          {tt("TalkingAvatar.Text.Label", "Anlatım metni")} <span>*</span>
        </label>
        <button
          type="button"
          className="add-sound-modal__narration-use"
          disabled={sceneTextLoading}
          style={{ opacity: sceneTextLoading ? 0.6 : 1 }}
          onClick={fetchSceneText}
        >
          {sceneTextLoading
            ? tt("Loading", "Yükleniyor…")
            : tt("TalkingAvatar.Text.FetchScene", "Sahne Metnini Getir")}
        </button>
      </div>
      <textarea
        ref={textareaRef}
        className="add-sound-modal__narration-textarea"
        rows={5}
        placeholder={tt("TalkingAvatar.Text.Placeholder", "Avatarın söyleyeceği metni yazın…")}
        value={textValue}
        onChange={(e) => {
          setSceneTextMissing(false);
          setSubmitError(null);
          onTextChange?.(e.target.value);
        }}
      />
      {summary}
      {sceneTextMissing && (
        <div className="asm-ta__alert asm-ta__alert--info">
          {tt("TalkingAvatar.Text.NoSceneText", "Bu sahnede anlatım metni bulunamadı.")}
        </div>
      )}

      <div className="asm-ta__section">
        <TalkingVoicePicker
          value={voice}
          onChange={handleVoiceChange}
          orgVoices={orgVoices || []}
          orgLoading={orgVoices === null}
          orgError={orgError}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          providers={voiceProviders}
          audioDriven={audioDriven}
          elevenLabsModel={elevenLabsModel}
          onElevenLabsModelChange={handleElevenLabsModelChange}
        />
        {voiceGenderMismatch(voice, avatarGender) && (
          <div className="asm-ta__alert asm-ta__alert--warn" role="note">
            {String(avatarGender).toLowerCase() === "male"
              ? tt(
                  "TalkingAvatar.Voice.GenderMismatchFemale",
                  "Seçili ses kadın sesi, avatar erkek. Avatar bu sesle konuşacak."
                )
              : tt(
                  "TalkingAvatar.Voice.GenderMismatchMale",
                  "Seçili ses erkek sesi, avatar kadın. Avatar bu sesle konuşacak."
                )}
          </div>
        )}
      </div>

      <div className="asm-ta__section add-sound-modal__narration-toggle">
        <div>
          <div className="add-sound-modal__narration-toggle-title">
            {tt("TransparentBackground", "Şeffaf Arka Plan")}
          </div>
          <div className="add-sound-modal__narration-toggle-sub">
            {tt("RemoveAvatarBackground", "Avatar arka planını kaldır")}
          </div>
        </div>
        <button
          type="button"
          aria-pressed={!!transparentBg}
          className={`add-sound-modal__narration-switch${
            transparentBg ? " add-sound-modal__narration-switch--active" : ""
          }`}
          onClick={() => {
            setSubmitError(null);
            onTransparentBgChange?.(!transparentBg);
          }}
        >
          <span />
        </button>
      </div>

      <div className="asm-av__advanced">
        <button
          type="button"
          className="asm-av__advanced-head"
          onClick={() => setAdvancedOpen((p) => !p)}
        >
          <span className="asm-av__advanced-title">
            {tt("TalkingAvatar.Advanced", "Gelişmiş ayarlar")}
          </span>
          <span className="asm-av__advanced-summary">
            <span className="asm-av__advanced-chip">
              {tt("TalkingAvatar.Model.Label", "Model")} · {model ? model.label || model.id : "…"}
            </span>
            <span className="asm-av__advanced-chip">
              {tt("TalkingAvatar.Aspect.Label", "En/Boy Oranı")} · {aspectRatio}
            </span>
            <span className="asm-av__advanced-chip">
              {tt("TalkingAvatar.Language", "Konuşma dili")} · {language}
            </span>
            <span className="asm-av__advanced-caret">
              {advancedOpen ? <LuChevronUp /> : <LuChevronDown />}
            </span>
          </span>
        </button>

        {advancedOpen && (
          <div className="asm-av__advanced-body">
            <div>
              <span className="asm-ta__field-label">
                {tt("TalkingAvatar.Model.Label", "Model")}
              </span>
              {models === null ? (
                <div className="asm-ta__meter">
                  {tt("TalkingAvatar.Model.Loading", "Modeller yükleniyor…")}
                </div>
              ) : modelsError ? (
                <div className="asm-ta__alert">
                  <span>{tt("TalkingAvatar.Model.LoadError", "Modeller yüklenemedi.")}</span>
                  <button type="button" className="asm-ta__alert-action" onClick={loadModels}>
                    {tt("TalkingAvatar.Model.Retry", "Tekrar dene")}
                  </button>
                </div>
              ) : (
                <div className="asm-ta__pills" role="radiogroup">
                  {models.map((m) => {
                    const hint = modelHint(m);
                    return (
                      <span key={m.id} title={hint || undefined}>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={modelId === m.id}
                          disabled={!!hint}
                          className={`asm-ta__pill${modelId === m.id ? " asm-ta__pill--on" : ""}`}
                          onClick={() => {
                            setSubmitError(null);
                            setModelId(m.id);
                          }}
                        >
                          <span>{m.label || m.id}</span>
                          {/* Why a model cannot be picked is visible, not tooltip-only. */}
                          {hint ? <span className="asm-ta__pill-hint">{hint}</span> : null}
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {ratioOptions.length > 0 && (
              <div>
                <span className="asm-ta__field-label">
                  {tt("TalkingAvatar.Aspect.Label", "En/Boy Oranı")}
                </span>
                <div className="asm-ta__pills" role="radiogroup">
                  {ratioOptions.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={aspectRatio === o.value}
                      className={`asm-ta__pill${aspectRatio === o.value ? " asm-ta__pill--on" : ""}`}
                      onClick={() => {
                        setSubmitError(null);
                        onAspectRatioChange?.(o.value);
                      }}
                    >
                      <span>{tt(...ASPECT_LABELS[o.value])}</span>
                      {o.cropped && (
                        <span className="asm-ta__pill-hint">
                          {tt("TalkingAvatar.Aspect.Cropped", "16:9 üretilip kareye kırpılır")}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="asm-ta__field-label" htmlFor="asm-ta-language">
                {tt("TalkingAvatar.Language", "Konuşma dili")}
              </label>
              <select
                id="asm-ta-language"
                className="asm-ta__select"
                value={language}
                onChange={(e) => {
                  setSubmitError(null);
                  setLanguageChoice(e.target.value);
                }}
              >
                {LANGUAGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {tt(o.key, o.fallback)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {submitError && (
        <div className="asm-ta__alert" role="alert">
          <span>{submitError.message}</span>
          {submitError.action && submitError.action.label && (
            <button
              type="button"
              className="asm-ta__alert-action"
              onClick={() => runAction(submitError.action)}
            >
              {submitError.action.label}
            </button>
          )}
        </div>
      )}

      {missing.length > 0 && !locked && (
        <div className="asm-ta__missing">
          {tt("TalkingAvatar.Missing.Title", "Oluşturmak için")}:{" "}
          {missing.map((k) => tt(`TalkingAvatar.Missing.${k}`, MISSING_COPY[k])).join(" · ")}
        </div>
      )}
    </div>
  );
});

TalkingVideoPanel.propTypes = {
  /** The chosen avatar's gender (library / AI-created): voices of the other gender are not preselected. */
  avatarGender: PropTypes.string,
  /** Video the "Test üretim" fake result shows (the chosen avatar's own video). */
  testVideoUrl: PropTypes.string,
  stepRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  locked: PropTypes.bool,
  navId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sceneIndex: PropTypes.number,
  source: PropTypes.object,
  text: PropTypes.string,
  onTextChange: PropTypes.func,
  soundList: PropTypes.array,
  loadSceneNarrationText: PropTypes.func,
  transparentBg: PropTypes.bool,
  onTransparentBgChange: PropTypes.func,
  aspectRatio: PropTypes.string,
  onAspectRatioChange: PropTypes.func,
  voiceProfile: PropTypes.object,
  onStatusChange: PropTypes.func,
  onInsufficientBalance: PropTypes.func,
};

TalkingVideoPanel.displayName = "TalkingVideoPanel";

export default TalkingVideoPanel;
